import React, { useMemo } from "react";
import type { StatusStepInterface } from "./stepperBase";
import { StepperBase } from "./stepperBase";
import "./styles.css";
import { ReactComponent as CheckBoldIcon } from "@/icons/svg/i-check-bold.svg";
import { useTheme } from "@material-ui/core/styles";
import type { TextColor } from "../text/text";
import { View, ViewBackgroundColor } from "components/view/view";
import { Text } from "components/text/text";
import { Icon } from "components/icon/icon";

interface ComponentProps {
  wizardSteps: string[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  paddingTop?: number;
  avoidIntercomAlignmentSwitchSides?: boolean;
}

export interface StepperViewProps {
  avoidIntercomAlignmentSwitchSides?: boolean;
  activeStep: number;
  stepIndex: number;
  label?: string;
  setActiveStep: (step: number) => void;
}

const StepView: React.FC<StepperViewProps> = ({
  avoidIntercomAlignmentSwitchSides,
  activeStep,
  stepIndex,
  label,
  setActiveStep
}: StepperViewProps) => {
  const isMobile = false;
  const isSelected = activeStep === stepIndex;
  const isCompleted = activeStep > stepIndex;
  const showStepLabel = !isMobile;

  const theme = useTheme();

  const stepStyle: {
    backgroundColor: string;
    outline?: string;
    color: TextColor;
  } = useMemo(() => {
    if (isSelected) {
      return {
        backgroundColor: theme.newPalette.success.main,
        color: "black"
      };
    }

    if (isCompleted) {
      return { backgroundColor: theme.newPalette.success.main, color: "black" };
    }

    return { backgroundColor: theme.newPalette.background.neutral1, color: "gray" };
  }, [theme, isSelected, isCompleted]);

  if (isMobile) {
    return (
      <View alignItems={"center"}>
        <View
          width={10}
          height={10}
          alignItems={"center"}
          justifyContent={"center"}
          borderRadius={100}
          style={{
            backgroundColor: stepStyle.backgroundColor,
            boxShadow: isSelected ? "0px 0px 5px rgba(55, 193, 134, 0.5)" : "none"
          }}
        />
      </View>
    );
  }

  const handleStepClick = () => {
    if (setActiveStep && isCompleted) {
      setActiveStep(stepIndex);
    }
  };

  return (
    <View
      alignItems={"center"}
      className={isCompleted ? "MuiStep-completed" : ""}
      cursor={isCompleted ? "pointer" : undefined}
      onClick={isCompleted ? handleStepClick : undefined}
      style={{ zIndex: 1000 }}
    >
      <View width={30} height={30} alignItems={"center"} justifyContent={"center"} borderRadius={100} style={stepStyle}>
        {isCompleted ? (
          <Icon Source={CheckBoldIcon} pathColor={"black"} height={12} className="p-2" />
        ) : (
          <Text color={stepStyle.color} fontSize={12}>
            {stepIndex !== undefined ? stepIndex + 1 : ""}
          </Text>
        )}
      </View>
      <View alignItems={"center"}>
        {showStepLabel && (
          <Text fontSize={14} align={"center"} value={label} marginTop={5} paddingLeft={10} paddingRight={10} />
        )}
      </View>
    </View>
  );
};

const WizardStepper: React.FC<ComponentProps> = ({
  wizardSteps,
  activeStep,
  setActiveStep,
  paddingTop = 20
}: ComponentProps) => {
  const isMobile = false;
  const theme = useTheme();

  const BackgroundLine = () => (
    <div
      style={{
        position: "absolute",
        width: "100%",
        left: 0,
        top: 15,
        backgroundColor: "rgba(122, 134, 153, 0.1)",
        height: 2,
        zIndex: 100
      }}
    />
  );

  const steps: StatusStepInterface[] = wizardSteps.map((currentStep, index) => {
    return {
      StepView: (
        <StepView label={currentStep} activeStep={activeStep} stepIndex={index} setActiveStep={setActiveStep} />
      ),
      connectorColor: activeStep >= index ? (theme.newPalette.success.main as ViewBackgroundColor) : "transparent"
    };
  });

  const renderSelectedStepLabel = () => (
    <View alignItems={"center"} marginBottom={20} marginTop={20}>
      <Text value={wizardSteps[activeStep]} />
    </View>
  );

  return (
    <View paddingTop={paddingTop}>
      {isMobile && renderSelectedStepLabel()}
      <View flexDirection={"row"}>
        {!isMobile ? <BackgroundLine /> : null}
        <StepperBase steps={steps} />
      </View>
    </View>
  );
};

export default WizardStepper;
